/**
 * @author michalstolarski
 * @since 2023-7-28
 */

import * as React from "react";
import styled from "styled-components";

import { TestWrapper } from "@ui/Atoms/TestWrapper";
import * as Grid from "@ui/Atoms/Grid";
import { text } from "@ui/helpers";

type Variant = "warning" | "danger" | "info"; // next possible: "success" | "primary" | "secondary

interface Props {
  testId?: string;
  className?: string;
  children: React.ReactNode;
  variant: Variant;
}

/**
 * Component's purpose is to display a notification bar with a simply message.
 * @param props.children - It's not wrapped by the Markdown component.
 */
const Bar = React.memo((props: Props): React.ReactElement => {
  return (
    <TestWrapper testId={props.testId}>
      <Container className={props.className} variant={props.variant}>
        <Grid.Container>
          <Grid.Row>
            <Grid.Column>{props.children}</Grid.Column>
          </Grid.Row>
        </Grid.Container>
      </Container>
    </TestWrapper>
  );
});

Bar.displayName = "NotificationBar";

const Message = styled.div`
  text-align: center;
`;

const Link = styled.a`
  text-decoration: underline;

  &,
  &:hover {
    color: currentColor;
  }
`;

const NotificationBar = { Bar, Message, Link };
export { NotificationBar, Variant as NotificationBarVariant };

const createBgColor = (variant: Variant, color: string): string =>
  `--notification-bar-bg-color--${variant}: ${color}`;
const createTextColor = (variant: Variant, color: string): string =>
  `--notification-bar-color--${variant}: ${color}`;

const Container = styled.div<Pick<Props, "variant">>`
  ${text("semibold_16")}

  /* variant -- warning */
  ${props => createTextColor("warning", props.theme.palette.grey800)};
  ${props => createBgColor("warning", props.theme.palette.orange500)};

  /* variant -- danger */
  ${props => createTextColor("danger", props.theme.palette.white)};
  ${props => createBgColor("danger", props.theme.palette.red700)};

  /* variant -- info */
  ${props => createTextColor("info", props.theme.palette.green900)};
  ${props => createBgColor("info", props.theme.palette.green100)};

  padding: ${props => props.theme.margins.base_x2} 0;

  color: var(
    --notification-bar-color--${props => props.variant},
    ${props => props.theme.palette.grey800}
  );
  background-color: var(
    --notification-bar-bg-color--${props => props.variant},
    transparent
  );
`;
